/**
 * Player-side lobby UI rendered on a phone. Profile editor (name, color,
 * avatar), ready toggle, and a compact roster of co-joiners.
 *
 * The component owns a debounced write-through to Firestore and localStorage
 * for the profile triplet so we don't spam updates while the user is typing.
 */

import {
  Badge,
  Box,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  TextInput,
  Title,
  UnstyledButton,
} from '@mantine/core'
import { IconCheck } from '@tabler/icons-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { PLAYER_COLOR_HEX } from '@mdev/common'
import type { PlayerColor, Room } from '@mdev/common'
import type { GameModule } from '../module'
import { saveProfile, type StoredProfile } from './profile'
import { heartbeat, setReady, updatePlayerProfile } from './service'

const HEARTBEAT_INTERVAL_MS = 10_000
const PROFILE_DEBOUNCE_MS = 350
const PLAYER_OFFLINE_MS = 30_000

const COLOR_OPTIONS: PlayerColor[] = [ 'red', 'blue', 'green', 'purple', 'orange', 'teal' ]

/** Curated avatar emoji set — mix of animals, faces, and pro icons. */
const AVATAR_OPTIONS: string[] = [
  '🦊', '🐱', '🐶', '🐼', '🐯', '🦁',
  '🐸', '🐵', '🦝', '🐨', '🦖', '🐙',
  '😎', '🤠', '🧑‍🚀', '🧙', '🥷', '🧛',
  '🕵️', '🚓', '🚇', '🚲', '🗺️', '🎩',
]

interface PlayerLobbyProps {
  room: Room<unknown>
  gameModule: GameModule<unknown, unknown>
  uid: string
}

export function PlayerLobby( props: PlayerLobbyProps ) {
  const { room, gameModule, uid } = props

  const me = room.players[ uid ]
  // Co-joiners, excluding myself and the host display. Sort by name (tie-break
  // uid) so the list is stable instead of reshuffling on every heartbeat.
  const others = useMemo(
    () =>
      Object.values( room.players )
        .filter( ( p ) => p.uid !== uid && p.uid !== room.hostUid )
        .sort( ( a, b ) => {
          const byName = ( a.displayName || '' ).localeCompare( b.displayName || '' )
          return byName !== 0 ? byName : ( a.uid || '' ).localeCompare( b.uid || '' )
        } ),
    [ room.players, uid, room.hostUid ],
  )

  // Local controlled-input state — initialized from the live room snapshot.
  const [ displayName, setDisplayName ] = useState<string>( me?.displayName ?? 'Player' )
  const [ color, setColor ] = useState<PlayerColor>( me?.color ?? 'red' )
  const [ avatar, setAvatar ] = useState<string>( me?.avatar ?? '🦊' )

  // Sync from server when the player is first added (or reconnects with stored values).
  const initializedRef = useRef( false )
  useEffect( () => {
    if ( initializedRef.current ) return
    if ( !me ) return
    setDisplayName( me.displayName )
    setColor( me.color )
    setAvatar( me.avatar )
    initializedRef.current = true
  }, [ me ] )

  // Debounced write-through to Firestore + localStorage.
  const lastSentRef = useRef<StoredProfile | null>( null )
  useEffect( () => {
    const profile: StoredProfile = { displayName, color, avatar }
    const last = lastSentRef.current
    if (
      last &&
      last.displayName === profile.displayName &&
      last.color === profile.color &&
      last.avatar === profile.avatar
    ) {
      return
    }

    const handle = window.setTimeout( () => {
      lastSentRef.current = profile
      saveProfile( profile )
      void updatePlayerProfile( room.code, uid, profile ).catch( ( err ) => {
        console.warn( '[PlayerLobby] updatePlayerProfile failed', err )
      } )
    }, PROFILE_DEBOUNCE_MS )

    return () => window.clearTimeout( handle )
  }, [ displayName, color, avatar, room.code, uid ] )

  // Heartbeat while we're in the lobby.
  useEffect( () => {
    void heartbeat( room.code, uid ).catch( () => {} )
    const id = window.setInterval( () => {
      void heartbeat( room.code, uid ).catch( () => {} )
    }, HEARTBEAT_INTERVAL_MS )
    return () => window.clearInterval( id )
  }, [ room.code, uid ] )

  function onToggleReady( next: boolean ) {
    void setReady( room.code, uid, next ).catch( ( err ) => {
      console.warn( '[PlayerLobby] setReady failed', err )
    } )
  }

  // Colors taken by other players are disabled to prevent collisions.
  const takenColors = useMemo(
    () => new Set( others.map( ( p ) => p.color ) ),
    [ others ],
  )

  const PlayerExtras = gameModule.PlayerLobbyExtras
  const ready = me?.ready ?? false

  return (
    <Box
      style={ { minHeight: '100vh', background: '#0B0F1A', color: 'white' } }
      p="md"
    >
      <Stack gap="lg">
        {/* Big room code */}
        <Stack gap={ 2 }>
          <Text
            size="xs"
            c="dimmed"
            tt="uppercase"
            style={ { letterSpacing: 2 } }
          >
            { gameModule.displayName }
          </Text>
          <Text
            size="xs"
            c="dimmed"
          >
            Room
          </Text>
          <Title
            order={ 1 }
            style={ {
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: '4rem',
              lineHeight: 1,
              letterSpacing: '0.4rem',
              color: 'white',
            } }
          >
            { room.code }
          </Title>
        </Stack>

        {/* Profile editor */}
        <Paper
          p="md"
          withBorder
          style={ { borderColor: '#1F2937', background: '#0F172A' } }
        >
          <Stack gap="md">
            <TextInput
              label="Your name"
              size="md"
              value={ displayName }
              onChange={ ( e ) => setDisplayName( e.currentTarget.value ) }
              maxLength={ 20 }
            />

            <Stack gap="xs">
              <Text
                size="sm"
                fw={ 500 }
              >
                Color
              </Text>
              <Group gap="xs">
                { COLOR_OPTIONS.map( ( c ) => {
                  const isMine = c === color
                  const taken = takenColors.has( c ) && !isMine
                  return (
                    <UnstyledButton
                      key={ c }
                      onClick={ () => {
                        if ( !taken ) setColor( c )
                      } }
                      disabled={ taken }
                      style={ {
                        width: 44,
                        height: 44,
                        borderRadius: 999,
                        background: PLAYER_COLOR_HEX[ c ],
                        opacity: taken ? 0.3 : 1,
                        cursor: taken ? 'not-allowed' : 'pointer',
                        border: isMine ? '3px solid white' : '3px solid transparent',
                        boxShadow: isMine ? '0 0 0 2px rgba(255,255,255,0.2)' : undefined,
                      } }
                      aria-label={ c }
                    />
                  )
                } ) }
              </Group>
            </Stack>

            <Stack gap="xs">
              <Text
                size="sm"
                fw={ 500 }
              >
                Avatar
              </Text>
              <SimpleGrid
                cols={ 6 }
                spacing="xs"
              >
                { AVATAR_OPTIONS.map( ( emoji ) => {
                  const selected = emoji === avatar
                  return (
                    <UnstyledButton
                      key={ emoji }
                      onClick={ () => setAvatar( emoji ) }
                      style={ {
                        height: 48,
                        borderRadius: 8,
                        textAlign: 'center',
                        fontSize: '1.75rem',
                        background: selected ? '#1F2937' : '#0B0F1A',
                        border: selected ? '2px solid white' : '1px solid #1F2937',
                      } }
                    >
                      { emoji }
                    </UnstyledButton>
                  )
                } ) }
              </SimpleGrid>
            </Stack>
          </Stack>
        </Paper>

        {/* Ready toggle */}
        <Paper
          p="md"
          withBorder
          style={ {
            borderColor: ready ? '#22C55E' : '#1F2937',
            background: ready ? 'rgba(34,197,94,0.08)' : '#0F172A',
          } }
        >
          <Group justify="space-between">
            <Stack gap={ 2 }>
              <Text
                size="lg"
                fw={ 600 }
              >
                { ready ? 'Ready!' : 'Mark yourself ready' }
              </Text>
              <Text
                size="xs"
                c="dimmed"
              >
                The host starts the game once everyone is ready.
              </Text>
            </Stack>
            <Switch
              size="xl"
              checked={ ready }
              onChange={ ( e ) => onToggleReady( e.currentTarget.checked ) }
            />
          </Group>
        </Paper>

        { PlayerExtras && (
          <Paper
            p="md"
            withBorder
            style={ { borderColor: '#1F2937', background: '#0F172A' } }
          >
            <PlayerExtras
              room={ room }
              uid={ uid }
            />
          </Paper>
        ) }

        {/* Compact roster of others */}
        { others.length > 0 && (
          <Stack gap="xs">
            <Text
              size="sm"
              c="dimmed"
              tt="uppercase"
              style={ { letterSpacing: 1 } }
            >
              Other players
            </Text>
            { others.map( ( p ) => {
              const offline = Date.now() - p.lastSeenMs > PLAYER_OFFLINE_MS
              return (
                <Paper
                  key={ p.uid }
                  p="sm"
                  withBorder
                  style={ {
                    borderColor: '#1F2937',
                    background: '#0F172A',
                    opacity: offline ? 0.45 : 1,
                    borderLeft: `3px solid ${ PLAYER_COLOR_HEX[ p.color ] }`,
                  } }
                >
                  <Group justify="space-between">
                    <Group gap="sm">
                      <Text style={ { fontSize: '1.5rem', lineHeight: 1 } }>{ p.avatar }</Text>
                      <Text fw={ 500 }>{ p.displayName || 'Player' }</Text>
                    </Group>
                    { p.ready ? (
                      <Badge
                        color="green"
                        variant="filled"
                        leftSection={ <IconCheck size={ 12 } /> }
                      >
                        Ready
                      </Badge>
                    ) : (
                      <Badge
                        color="yellow"
                        variant="light"
                      >
                        Not ready
                      </Badge>
                    ) }
                  </Group>
                </Paper>
              )
            } ) }
          </Stack>
        ) }
      </Stack>
    </Box>
  )
}
